/**
 * Date conversion between the sheet and the app.
 *
 * The sheet stores dates as real date values, which the Sheets API returns as
 * serial numbers when read with UNFORMATTED_VALUE: days since 1899-12-30.
 *
 * We deliberately do not read FORMATTED_VALUE. This sheet displays dates as
 * DD.MM.YYYY, and parsing a localised display string is the kind of thing that
 * works until someone changes the spreadsheet locale. Serial numbers have no
 * locale.
 *
 * Writes go the same way: we send the serial number, not an ISO string, with
 * valueInputOption=RAW. A string would land in the cell as text, which would
 * break both the date formatting and the "Due days" formula that subtracts it.
 */

const EPOCH = Date.UTC(1899, 11, 30);
const DAY = 86400000;

/** Sheet serial number to "YYYY-MM-DD". Returns "" for anything unusable. */
export function serialToIso(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(EPOCH + Math.round(value) * DAY).toISOString().slice(0, 10);
  }
  // Tolerate a cell someone typed as text, in ISO or DD.MM.YYYY form.
  if (typeof value === "string") {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const dotted = text.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
    if (dotted) {
      const [, d, m, y] = dotted;
      return `${y}-${m!.padStart(2, "0")}-${d!.padStart(2, "0")}`;
    }
  }
  return "";
}

/** "YYYY-MM-DD" to a sheet serial number. Returns "" for an empty date. */
export function isoToSerial(iso: string): number | "" {
  if (!iso) return "";
  const parts = iso.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "";
  const [y, m, d] = parts as [number, number, number];
  return Math.round((Date.UTC(y, m - 1, d) - EPOCH) / DAY);
}

/** Local midnight today, used for every overdue and due-soon comparison. */
export function today(): Date {
  return new Date(new Date().toDateString());
}

export function daysUntil(iso: string, from: Date = today()): number | null {
  if (!iso) return null;
  const parts = iso.split("-").map(Number);
  if (parts.length !== 3) return null;
  const [y, m, d] = parts as [number, number, number];
  const target = new Date(y, m - 1, d);
  return Math.round((target.getTime() - from.getTime()) / DAY);
}

/** "2 days late", "Today", "Tomorrow", "Friday", or a short date. */
export function dueLabel(iso: string, from: Date = today()): string {
  const days = daysUntil(iso, from);
  if (days === null) return "";
  if (days < 0) {
    const n = Math.abs(days);
    return `${n} ${n === 1 ? "day" : "days"} late`;
  }
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  const parts = iso.split("-").map(Number) as [number, number, number];
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  if (days <= 6) return date.toLocaleDateString(undefined, { weekday: "long" });
  return shortDate(iso);
}

/** "21 Apr" */
export function shortDate(iso: string): string {
  if (!iso) return "";
  const parts = iso.split("-").map(Number) as [number, number, number];
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(
    undefined,
    { day: "numeric", month: "short" }
  );
}

export function todayIso(): string {
  const d = today();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** How urgent a deadline is. Drives the colour on a task card. */
export type Urgency = "ok" | "warn" | "late";

/**
 * Green above 30 days, amber from 14 to 30, red below 14 and on into
 * overdue. Returns null when there is no deadline to judge.
 */
export function urgency(iso: string, from: Date = today()): Urgency | null {
  const days = daysUntil(iso, from);
  if (days === null) return null;
  if (days > 30) return "ok";
  if (days >= 14) return "warn";
  return "late";
}

/** "32 days left", "1 day left", "Today", "5 days late". */
export function daysLeftLabel(iso: string, from: Date = today()): string {
  const days = daysUntil(iso, from);
  if (days === null) return "";
  if (days === 0) return "Today";
  if (days < 0) {
    const n = Math.abs(days);
    return `${n} ${n === 1 ? "day" : "days"} late`;
  }
  return `${days} ${days === 1 ? "day" : "days"} left`;
}
